/**
 * @typedef CorporateList
 * @type {Object}
 * @property {number} author_id
 * @property {string} author_name
 * @property {string} active_date
 * @property {string} author_type
 * @property {string} article_info
 * @property {string} status
 */

/**
 * @typedef ErrorState
 * @type {?string|null}
 */

/** @typedef {'indonesia'|'english'} AuthorLanguage */

/**
 * @typedef {Object} CorporateAuthorListQuery
 * @property {number} page
 * @property {number} per_page
 * @property {string} author_name
 * @property {AuthorLanguage} language
 */

/**
 * @typedef {Object} CorporateAuthor
 * @property {number} author_id
 * @property {string} author_name
 * @property {string} author_slug
 * @property {string} author_desc
 * @property {string} author_facebook_url
 * @property {string} author_x_url
 * @property {string} author_in_url
 * @property {string|null} author_banner
 * @property {string|null} author_banner_mobile
 * @property {string|null} author_avatar
 * @property {string} author_status
 * @property {AuthorLanguage|null|undefined} language
 */

/**
 * @typedef {Object} CorporateAuthorForm
 * @property {string} author_name
 * @property {string} author_slug
 * @property {string} author_desc
 * @property {string} author_facebook_url
 * @property {string} author_x_url
 * @property {string} author_in_url
 * @property {File|string|null} author_banner
 * @property {File|string|null} author_banner_mobile
 * @property {File|string|null} author_avatar
 * @property {string} author_status
 * @property {AuthorLanguage} language
 */

/**
 * @typedef {Object} CorporateAuthorHydrationMeta
 * @property {boolean} hadPersistedLanguage
 * @property {boolean} languageDirty
 * @property {AuthorLanguage|null} persistedLanguage
 */

/**
 * @template T
 * @typedef {Object} RepositoryResult
 * @property {T} data
 * @property {string|null} err
 */

/**
 * @typedef {Object} MutationResult
 * @property {CorporateAuthor|null} data
 * @property {string} message
 * @property {string|null} err
 */

/**
 * @typedef {Object} CorporateAuthorRepository
 * @property {(query: CorporateAuthorListQuery) => Promise<RepositoryResult<CorporateAuthor[]>>} getAll
 * @property {(id: number|string) => Promise<RepositoryResult<CorporateAuthor|null>>} getOne
 * @property {(name: string, language?: AuthorLanguage) => Promise<RepositoryResult<boolean>>} checkAvailable
 * @property {(command: CorporateAuthorForm) => Promise<MutationResult>} createOne
 * @property {(id: number|string, command: Partial<CorporateAuthorForm>) => Promise<MutationResult>} updateOne
 */

export const AUTHOR_LANGUAGES = ['indonesia', 'english']

export const DEFAULT_AUTHOR_LANGUAGE = 'indonesia'

/**
 * @param {*} value
 * @return {value is AuthorLanguage}
 */
export function isAuthorLanguage(value) {
  return AUTHOR_LANGUAGES.includes(value)
}

/**
 * @param {AuthorLanguage} [language]
 * @return {CorporateAuthorForm}
 */
export function createEmptyAuthorForm(language = DEFAULT_AUTHOR_LANGUAGE) {
  return {
    author_name: "",
    author_slug: "",
    author_desc: "",
    author_facebook_url: "",
    author_x_url: "",
    author_in_url: "",
    author_banner: null,
    author_banner_mobile: null,
    author_avatar: null,
    author_status: "ACTIVE",
    language,
  }
}
